import { supabase } from '../lib/supabase';
import apiClient from './client';
import { TicketService } from './supabase/ticketSupabaseApi';

export interface TicketFilterParams {
  status?: string;
  priority?: string;
  assignee_id?: string;
  requester_id?: string;
  team_id?: string;
  organization_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateTicketPayload {
  subject: string;
  description: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Urgent' | string;
  impact?: 'Low' | 'Medium' | 'High' | 'Critical' | string;
  urgency?: 'Low' | 'Medium' | 'High' | 'Critical' | string;
  category?: string;
  category_id?: string;
  subcategory_id?: string;
  team_id?: string;
  assignee_id?: string;
  requester_id?: string;
  requester_name?: string;
  requester_email?: string;
  organization_id?: string;
  module?: string;
  requestType?: string;
  asset_id?: string;
  cc?: string[];
  bcc?: string[];
  source?: 'Web' | 'Email' | 'Phone' | 'Chat' | 'API' | 'Technician' | string;
  source_type?: string;
  custom_fields?: Record<string, any>;
  attachments?: File[];
}

export interface UpdateTicketPayload {
  subject?: string;
  description?: string;
  status?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Urgent' | string;
  category_id?: string;
  subcategory_id?: string;
  team_id?: string;
  assignee_id?: string;
  module?: string;
  resolution_code?: string;
  closure_notes?: string;
  custom_fields?: Record<string, any>;
}

export const ticketsApi = {
  getTickets: async (params?: TicketFilterParams) => {
    if (supabase) {
      return await TicketService.getTickets({
        status: params?.status,
        priority: params?.priority,
        assigneeId: params?.assignee_id,
        requesterId: params?.requester_id,
        teamId: params?.team_id,
        search: params?.search,
        page: params?.page,
        limit: params?.limit
      });
    }

    try {
      const { data } = await apiClient.get<any[]>('/tickets', { params });
      return data;
    } catch {
      return [];
    }
  },

  getTicketById: async (id: string) => {
    if (supabase) {
      try {
        const sbTicket = await TicketService.getTicketById(id);
        if (sbTicket) {
          return { success: true, ticket: sbTicket };
        }
      } catch (fbErr) {
        console.warn('Direct Supabase lookup in getTicketById:', fbErr);
      }
    }

    try {
      const { data } = await apiClient.get<any>(`/tickets/${id}`);
      return data;
    } catch (err: any) {
      throw err;
    }
  },

  createTicket: async (payload: CreateTicketPayload) => {
    if (supabase) {
      const ticket = await TicketService.createTicket({
        subject: payload.subject,
        description: payload.description,
        priority: payload.priority,
        impact: payload.impact,
        urgency: payload.urgency,
        category: payload.category,
        category_id: payload.category_id,
        subcategory_id: payload.subcategory_id,
        team_id: payload.team_id,
        assignee_id: payload.assignee_id,
        requester_id: payload.requester_id,
        requester_email: payload.requester_email,
        requester_name: payload.requester_name,
        organization_id: payload.organization_id,
        module: payload.module || payload.requestType,
        source: payload.source,
        source_type: payload.source_type,
        asset_id: payload.asset_id,
        cc: payload.cc,
        bcc: payload.bcc,
        custom_fields: payload.custom_fields,
        attachments: payload.attachments
      });
      return { success: true, ticket };
    }

    const { data } = await apiClient.post<any>('/tickets', payload);
    return data;
  },

  updateTicket: async (id: string, updates: UpdateTicketPayload) => {
    if (supabase) {
      const ticket = await TicketService.updateTicket(id, updates);
      return { success: true, ticket };
    }

    const { data } = await apiClient.put<any>(`/tickets/${id}`, updates);
    return data;
  },

  assignTicket: async (id: string, assigneeId: string | null) => {
    if (supabase) {
      return await TicketService.assignTicket(id, assigneeId);
    }

    try {
      const { data } = await apiClient.patch<{ success: boolean; data?: { ticket: any }; ticket?: any }>(
        `/tickets/${id}/assignment`,
        { assigneeId }
      );
      return data.data?.ticket || data.ticket;
    } catch (err) {
      throw err;
    }
  },

  deleteTicket: async (id: string) => {
    if (supabase) {
      const { error } = await supabase.from('tickets').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/tickets/${id}`);
    return data;
  },

  getTicketHistory: async (ticketId: string) => {
    if (supabase) {
      const { data, error } = await supabase
        .from('ticket_events')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (!error && data) return data;
    }

    try {
      const { data } = await apiClient.get<any[]>(`/tickets/${ticketId}/history`);
      return data;
    } catch {
      return [];
    }
  },

  addComment: async (ticketId: string, content: string, isInternal = false) => {
    if (supabase) {
      const comment = await TicketService.addComment({ ticket_id: ticketId, content, is_internal: isInternal });
      return { success: true, comment };
    }

    const { data } = await apiClient.post<any>(`/tickets/${ticketId}/comments`, { content, isInternal });
    return data;
  },

  getTicketComments: async (ticketId: string) => {
    if (supabase) {
      const { data, error } = await supabase
        .from('ticket_comments')
        .select('*, author:profiles!ticket_comments_author_id_fkey(full_name, email, role)')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (!error && data) return data;
    }

    try {
      const { data } = await apiClient.get<any[]>(`/tickets/${ticketId}/comments`);
      return data;
    } catch {
      return [];
    }
  },

  getMyTickets: async () => {
    if (supabase) {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user?.id;
      if (uid) {
        return await TicketService.getTickets({ requesterId: uid });
      }
      return [];
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; tickets: any[] }>('/tickets/my');
      return data.tickets || [];
    } catch {
      return [];
    }
  },

  getUserDashboardStats: async () => {
    if (supabase) {
      const tickets = await TicketService.getTickets({ limit: 100 });
      const open = tickets.filter(t => t.status === 'Open').length;
      const inProgress = tickets.filter(t => t.status === 'In Progress').length;
      const resolved = tickets.filter(t => t.status === 'Resolved').length;
      const closed = tickets.filter(t => t.status === 'Closed').length;
      return {
        success: true,
        stats: { total: tickets.length, open, inProgress, resolved, closed },
        recentTickets: tickets.slice(0, 10)
      };
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; stats: any; recentTickets: any[] }>('/tickets/dashboard/stats');
      return data;
    } catch {
      return { success: true, stats: { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 }, recentTickets: [] };
    }
  },

  // Technician Endpoints
  getTechnicianDashboard: async () => {
    if (supabase) {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user?.id;
      const allTickets = await TicketService.getTickets({ limit: 200 });
      const myTickets = allTickets.filter(t => t.assignee_id === uid);
      const unassignedTickets = allTickets.filter(t => !t.assignee_id);
      return {
        success: true,
        stats: {
          assigned: myTickets.length,
          unassigned: unassignedTickets.length,
          inProgress: myTickets.filter(t => t.status === 'In Progress').length,
          resolvedToday: myTickets.filter(t => t.status === 'Resolved').length
        },
        myTickets: myTickets.slice(0, 10),
        unassignedTickets: unassignedTickets.slice(0, 10)
      };
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; stats: any; myTickets: any[]; unassignedTickets: any[] }>('/technician/dashboard');
      return data;
    } catch {
      return { success: true, stats: { assigned: 0, unassigned: 0, inProgress: 0, resolvedToday: 0 }, myTickets: [], unassignedTickets: [] };
    }
  },

  getTechnicianTickets: async (params?: { scope?: 'my' | 'unassigned' | 'all'; status?: string; priority?: string }) => {
    if (supabase) {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user?.id;
      let assigneeId: string | undefined = undefined;
      if (params?.scope === 'my' && uid) assigneeId = uid;

      return await TicketService.getTickets({
        status: params?.status,
        priority: params?.priority,
        assigneeId
      });
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; tickets: any[] }>('/technician/tickets', { params });
      return data.tickets || [];
    } catch {
      return [];
    }
  },

  getTechnicianTicket: async (id: string) => {
    if (supabase) {
      return await TicketService.getTicketById(id);
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; ticket: any }>(`/technician/tickets/${id}`);
      return data.ticket;
    } catch (err) {
      throw err;
    }
  },

  assignTechnicianTicket: async (id: string, assigneeId?: string | null) => {
    if (supabase) {
      return await TicketService.assignTicket(id, assigneeId || null);
    }

    try {
      const { data } = await apiClient.post<{ success: boolean; ticket: any }>(`/technician/tickets/${id}/assign`, { assigneeId });
      return data.ticket;
    } catch (err) {
      throw err;
    }
  },

  updateTechnicianStatus: async (id: string, status: string, notes?: string) => {
    if (supabase) {
      return await TicketService.updateStatus(id, status, notes, true);
    }

    try {
      const { data } = await apiClient.put<{ success: boolean; ticket: any }>(`/technician/tickets/${id}/status`, { status, notes });
      return data.ticket;
    } catch (err) {
      throw err;
    }
  },

  resolveTechnicianTicket: async (id: string, payload: { resolution_code?: string; closure_notes?: string; comments?: string }) => {
    if (supabase) {
      await TicketService.updateTicket(id, {
        resolution_code: payload.resolution_code,
        closure_notes: payload.closure_notes
      });
      return await TicketService.updateStatus(id, 'Resolved', payload.comments, false);
    }

    try {
      const { data } = await apiClient.post<{ success: boolean; ticket: any }>(`/technician/tickets/${id}/resolve`, payload);
      return data.ticket;
    } catch (err) {
      throw err;
    }
  },

  addTechnicianComment: async (id: string, content: string, is_internal = false) => {
    if (supabase) {
      return await TicketService.addComment({ ticket_id: id, content, is_internal });
    }

    try {
      const { data } = await apiClient.post<{ success: boolean; comment: any }>(`/technician/tickets/${id}/comments`, { content, is_internal });
      return data.comment;
    } catch (err) {
      throw err;
    }
  },

  // Admin Endpoints
  getAdminDashboard: async (organizationId?: string) => {
    if (supabase) {
      const tickets = await TicketService.getTickets({ limit: 500 });
      const open = tickets.filter(t => t.status === 'Open').length;
      const inProgress = tickets.filter(t => t.status === 'In Progress').length;
      const resolved = tickets.filter(t => t.status === 'Resolved').length;
      const closed = tickets.filter(t => t.status === 'Closed').length;
      return {
        success: true,
        stats: { total: tickets.length, open, inProgress, resolved, closed },
        recentTickets: tickets.slice(0, 15)
      };
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; stats: any; recentTickets: any[] }>('/admin/dashboard', { params: { organizationId } });
      return data;
    } catch {
      return { success: true, stats: { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 }, recentTickets: [] };
    }
  },

  getAdminTickets: async (params?: TicketFilterParams) => {
    if (supabase) {
      return await TicketService.getTickets(params);
    }

    try {
      const { data } = await apiClient.get<{ success: boolean; tickets: any[] }>('/admin/tickets', { params });
      return data.tickets || [];
    } catch {
      return [];
    }
  },

  // SOC Endpoints
  getSocDashboardStats: async () => {
    if (supabase) {
      const socTickets = await TicketService.getTickets({ sourceType: 'SENTINEL', limit: 100 });
      return {
        success: true,
        totalIncidents: socTickets.length,
        activeIncidents: socTickets.filter(t => t.status !== 'Closed' && t.status !== 'Resolved').length,
        criticalAlerts: socTickets.filter(t => t.priority === 'Urgent' || t.priority === 'P1').length,
        recentIncidents: socTickets.slice(0, 10)
      };
    }

    try {
      const { data } = await apiClient.get<any>('/soc/dashboard');
      return data;
    } catch {
      return { success: true, totalIncidents: 0, activeIncidents: 0, criticalAlerts: 0, recentIncidents: [] };
    }
  },

  retryNotification: async (notificationId: string) => {
    if (supabase) {
      try {
        await supabase
          .from('notifications')
          .update({ read: false, updated_at: new Date().toISOString() })
          .eq('id', notificationId);
        return { success: true, message: 'Notification queued for redelivery.' };
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.post<{ success: boolean; message: string }>(`/email/notifications/${notificationId}/retry`);
      return data;
    } catch {
      return { success: true, message: 'Notification retry acknowledged.' };
    }
  }
};
